import { useGetReviewerPermissions } from '@exam-center/admin-exam-location-management';
import {
  AdminSearchMyExamRoomRequestInfosStep,
  useAdminSearchMyExamRoomRequestInfosQuery,
} from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import { type GridEventListener, type GridRowParams, NoRowsOverlay } from '@shared/components';
import { ORDER_TYPE } from '@shared/graphql';
import { useMemo } from 'react';

import {
  BaseAdminExamLocationRequestList,
  EmptyExamLocationRequestsOverlay,
  inReviewColumns,
  type InReviewExamLocationRequest,
} from '../../components';
import { useAdminExamLocationRequestListSearchParams } from '../../hooks';
import { mapExamLocationRequestData } from '../../utils';

export default function InReviewAdminExamLocationRequestList() {
  const [
    { page, pageSize, sortOrder, search, province, examCenters },
    { changePageNumber, changePageSize, changeSorting },
  ] = useAdminExamLocationRequestListSearchParams();
  const navigate = useNavigate();

  const { view } = useGetReviewerPermissions();

  const { data, loading } = useAdminSearchMyExamRoomRequestInfosQuery({
    variables: {
      adminSearchMyExamRoomRequestInfosInput: {
        filter: {
          step: AdminSearchMyExamRoomRequestInfosStep.InReview,
          locationName: search,
          ...(examCenters.length > 0 ? { examCenterCodes: examCenters } : {}),
          ...(province.length > 0 ? { provinceId: province } : {}),
        },
        paging: {
          currentPage: page,
          pageSize: pageSize,
        },
        order: {
          field: 'requestedAt',
          type: ORDER_TYPE[sortOrder],
        },
      },
    },
  });

  const { examLocationRequests, totalCount } = useMemo(() => {
    const examLocationRequests = mapExamLocationRequestData(
      data?.ecAdminSearchMyExamRoomRequestInfos?.examRoomRequestInfos ?? [],
    );
    const totalCount = data?.ecAdminSearchMyExamRoomRequestInfos.total ?? 0;

    return { examLocationRequests, totalCount };
  }, [data]);

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<InReviewExamLocationRequest>,
  ) => {
    navigate('/admin/exam-location/requests/:roomRequestId', {
      params: {
        roomRequestId: params.row.id,
      },
    });
  };

  const isSearchingOrFiltering = search.length > 0 || province.length > 0 || examCenters.length > 0;

  return (
    <BaseAdminExamLocationRequestList
      columns={inReviewColumns}
      initialState={{
        columns: {
          columnVisibilityModel: {
            firstVerdict: view.supervisor,
            secondVerdict: view.supervisor,
          },
        },
      }}
      rows={examLocationRequests}
      loading={loading}
      sortingMode='server'
      onRowClick={handleRowClick}
      slots={{
        noRowsOverlay: () => {
          if (isSearchingOrFiltering) {
            return <NoRowsOverlay />;
          }

          return (
            <EmptyExamLocationRequestsOverlay title='ยังไม่มีคำขออนุมัติสถานที่สอบที่รออนุมัติ' />
          );
        },
      }}
      slotProps={{
        footer: {
          total: totalCount,
          pagination: {
            page: page,
            count: totalCount,
            rowsPerPage: pageSize,
            onPageChange: (_, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      onSortModelChange={(sorting) => {
        const sortModel = sorting[0];
        if (sortModel?.field && sortModel?.sort) {
          return changeSorting({
            sortOrder: sortModel.sort,
            sortBy: sortModel.field,
          });
        }

        return changeSorting({
          sortOrder: null,
          sortBy: null,
        });
      }}
    />
  );
}
